<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: FamilySection                                         |
| Section: components/Person                                       |
| Purpose: Family relationship section for spouse and children.    |
+------------------------------------------------------------------+
-->
<template>
    <div v-if="localFamilies.length > 0 && localFamilies.some(family => family.children && family.children.length > 0)"
        class="PersonCardGroup_container" style="background-color: var(--brand-bg-8);">
        <div class="PersonCardGroup_wrapper wrapper">
            <div class="FamilySection_heading margin-fix">
                <TitleGroup :title="$t('familySection.sectionTitle')" :isWhiteText="true"
                    :showLink="false" />
                <button v-if="hasDescendants" type="button" class="FamilySection_daboville_toggle"
                    :aria-expanded="String(activeDescendantView === 'daboville')"
                    :aria-controls="dabovillePanelId" @click="toggleDescendantView">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                        stroke-linejoin="round" aria-hidden="true">
                        <path d="M8 6h13M8 12h13M8 18h13" />
                        <path d="M3 6h.01M3 12h.01M3 18h.01" />
                    </svg>
                    {{ activeDescendantView === 'daboville'
                        ? $t('familySection.treeView')
                        : $t('familySection.dAbovilleView') }}
                </button>
            </div>

            <DescendantTreePanel v-if="hasDescendants" v-show="activeDescendantView === 'tree'"
                class="FamilySection_descendant_tree" :personId="personID"
                :initialGenerations="1" :fitToContent="true" :familyGroups="localFamilies"
                :initialRootPerson="person" />

            <div v-if="hasDescendants && hasOpenedDaboville" v-show="activeDescendantView === 'daboville'"
                :id="dabovillePanelId" class="FamilySection_daboville_container">
                <DescendantDAbovilleList :personId="personID" />
            </div>
            <div v-if="shouldShowFamilyCardList" class="FamilySection_children_cards">
                <div class="flex-wrap" v-for="family in localFamilies" :key="family.familyID">
                    <!-- Title for each family -->
                    <TitleGroup :label="$t('familySection.sectionTitle')" :isWhiteText="true" :showH3Only="true">
                        {{ $t('familySection.withSpouse') }}
                        <a :href="getSpouseHref(family)"
                            class="spouse-link">
                            {{ getSpouseDisplayName(family) }}
                        </a>
                    </TitleGroup>

                    <!-- Children for each family -->
                    <div class="PersonCardGroup">
                        <PersonCard v-for="child in family.children" :key="child.personID || child.nodeID" :photoSrc="child.photoSrc"
                            :name="isPrivatePerson(child) ? this.$t('searchResults.privacyHidden') : child.name"
                            :years="child.years" :gender="child.sex === 'M' ? 'male' : 'female'"
                            :href="child.href || localePath(`/person/${child.personID}`)"
                            :personID="child.personID" :firstname="child.firstname" :lastname="child.lastname"
                            :living="child.living"
                            :placeholder="child.placeholder"
                            :isSmall="true" :class="{ 'PersonCard_small_guest': isPrivatePerson(child) }" />
                        <PersonCard v-if="family.children.length === 0" :photoSrc="defaultPhotoSrc"
                            :name="$t('familySection.noChildren')" :years="'N/A'" :gender="'unknown'"
                            :href="'javascript:void(0)'" :isSmall="true" class="PersonCard--default" />
                    </div>
                </div>
            </div>
        </div>
    </div>
    <!-- <div class="ImageDivider_container" ref="imageDivider"
        v-if="localFamilies.length > 0 && localFamilies.some(family => family.children && family.children.length > 0)">
        <div class="ImageDivider_wrapper wrapper">
            <div class="TitleBtnGroup">
                <h2>Meer weten over het nageslacht?</h2>
                <a :href="`/person/${personID}/descendants`" class="BtnPrimary">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="#FFF">
                        <path
                            d="M15.7063 9.20627C16.0969 8.81565 16.0969 8.18127 15.7063 7.79065L11.7063 3.79065C11.3156 3.40002 10.6812 3.40002 10.2906 3.79065C9.9 4.18127 9.9 4.81565 10.2906 5.20627L12.5844 7.50002H1C0.446875 7.50002 0 7.9469 0 8.50002C0 9.05315 0.446875 9.50002 1 9.50002H12.5844L10.2906 11.7938C9.9 12.1844 9.9 12.8188 10.2906 13.2094C10.6812 13.6 11.3156 13.6 11.7063 13.2094L15.7063 9.2094V9.20627Z"
                            fill="#FFF" />
                    </svg>
                    Bekijk alle afstammelingen
                </a>
            </div>
        </div>
    </div> -->
</template>

<script>
import PersonCard from '@/atoms/PersonCard.vue';
// import SpouseTitleGroup from '@/atoms/SpouseTitleGroup.vue';
import TitleGroup from '@/atoms/TitleGroup.vue';
import DescendantTreePanel from '@/components/Person/DescendantTreePanel.vue';
import DescendantDAbovilleList from '@/components/Person/DescendantDAbovilleList.vue';
// import BtnPrimary from '@/atoms/BtnPrimary.vue';
// import { getGenderBasedText } from '@/utils/textUtils'; // replaced with $t gendered keys
import { formatYears, extractSortableDate } from '@/utils/dateUtils';
import { getPhotoSrcWithFallback } from '@/utils/photoUtils';
import { useAuth } from '@/composables/useAuth';
import { useLocale } from '@/composables/useLocale';
import { getPersonNodeKey, isLivingPlaceholder, isPrivateLivingPerson } from '@/utils/personPrivacy';

export default {
    name: 'SpouseSection',
    components: {
        PersonCard,
        // SpouseTitleGroup,
        // BtnPrimary,
        TitleGroup,
        DescendantTreePanel,
        DescendantDAbovilleList
    },
    setup() {
        // Injecting properties from App.vue using the useAuth composable
        const { authToken, username, logout } = useAuth();
        const { localePath } = useLocale();

        return {
            authToken,
            username,
            logout,
            localePath,
        };
    },
    props: {
        families: {
            type: Array,
            required: true,
        },
        personID: {
            type: String,
            required: true,
        },
        person: {
            type: Object,
            required: true,
        },
        openMediaOverlay: {
            type: Function,
            required: true,
        },
        familyInfo: {
            type: Array,
            required: true
        },
        showFamilySectionChildCards: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            localFamilies: [],
            familyDocuments: [], // Store fetched documents
            marriageDate: '', // New property to store marriage date
            marriagePlace: '', // New property to store marriage place
            selectedFamilyIndex: 0, // Track the selected family index
            activeDescendantView: 'tree',
            hasOpenedDaboville: false
        };
    },
    computed: {
        familyID() {
            return this.localFamilies.length > 0 ? this.localFamilies[0].familyID : '';
        },
        selectedFamily() {
            return this.localFamilies[this.selectedFamilyIndex] || {};
        },
        hasMultipleSpouses() {
            return this.localFamilies.length > 1;
        },
        label() {
            return "Keluarga en keturunan";
        },
        titlePrefix() {
            const sex = this.person?.sex;
            if (sex === 'M') return this.$t('familySection.titlePrefix.male');
            if (sex === 'F') return this.$t('familySection.titlePrefix.female');
            return this.$t('familySection.titlePrefix.unknown');
        },
        selectedSpouseName() {
            const family = this.selectedFamily;
            return family.husband === this.personID ? family.wifeName : family.husbandName;
        },
        sortedChildren() {
            // Gather all children from all families
            const allChildren = this.localFamilies.flatMap(family => family.children || []);

            // Sort children by birth date
            return allChildren.slice().sort((a, b) => {
                // Prefer birthdate, but fall back to baptism (altbirthdatetr) when birthdate is missing.
                const dateA = extractSortableDate(a.birthdatetr) || extractSortableDate(a.altbirthdatetr);
                const dateB = extractSortableDate(b.birthdatetr) || extractSortableDate(b.altbirthdatetr);

                if (dateA && dateB) {
                    return dateA - dateB; // Sort ascending by date
                } else if (dateA) {
                    return -1; // Place a non-null date before null
                } else if (dateB) {
                    return 1; // Place null after non-null date
                } else {
                    return 0; // Keep the original order if both dates are null
                }
            });
        },
        hasDescendants() {
            return this.localFamilies.some(family => family.children && family.children.length > 0);
        },
        shouldShowFamilyCardList() {
            return !this.hasDescendants || this.showFamilySectionChildCards;
        },
        dabovillePanelId() {
            return `family-daboville-panel-${this.personID}`;
        },
        filteredChildren() {
            // Get the selected family based on the current selectedFamilyIndex
            const selectedFamily = this.selectedFamily;

            // If no selected family or no children, return an empty array
            if (!selectedFamily || !selectedFamily.children) {
                return [];
            }

            // Filter to only include children in the selected family
            const selectedKeys = new Set(selectedFamily.children.map(getPersonNodeKey).filter(Boolean));
            return this.sortedChildren.filter(child => selectedKeys.has(getPersonNodeKey(child)));
        }
    },
    watch: {
        families: {
            immediate: true,
            handler() {
                this.processFamilies();
            }
        },
        familyInfo() {
            this.processFamilies();
        },
        personID() {
            this.activeDescendantView = 'tree';
            this.hasOpenedDaboville = false;
            this.processFamilies();
        }
    },
    methods: {
        toggleDescendantView() {
            if (this.activeDescendantView === 'tree') {
                this.hasOpenedDaboville = true;
                this.activeDescendantView = 'daboville';
                return;
            }

            this.activeDescendantView = 'tree';
        },
        isPrivatePerson(person) {
            return isPrivateLivingPerson(person, this.authToken);
        },
        getSpousePlaceholder(family) {
            return family.husband === this.personID
                ? family.wifePlaceholder
                : family.husbandPlaceholder;
        },
        getSpouseDisplayName(family) {
            if (isLivingPlaceholder(this.getSpousePlaceholder(family))) {
                return this.$t('privacy.livingPlaceholder');
            }
            return (family.husband === this.personID ? family.wifeName : family.husbandName)
                || this.$t('familySection.unknownSpouse');
        },
        getSpouseHref(family) {
            if (isLivingPlaceholder(this.getSpousePlaceholder(family))) return 'javascript:void(0)';
            const spouseID = family.husband === this.personID ? family.wife : family.husband;
            return spouseID ? this.localePath(`/person/${spouseID}`) : 'javascript:void(0)';
        },
        generateSpouseTitle(family) {
            const spouseName =
                family.husband === this.personID
                    ? family.wifeName || 'Onbekende Spouse'
                    : family.husbandName || 'Onbekende Spouse';

            const spouseHref =
                family.husband === this.personID
                    ? this.localePath(`/person/${family.wife}`)
                    : this.localePath(`/person/${family.husband}`);

            // Return the dynamic title with the span and link
            return `Met <a href="${spouseHref}" class="spouse-link">${spouseName}</a>`;
        },
        selectNextFamily() {
            // Switch to the next family, looping back to the first if at the end.
            this.selectedFamilyIndex = (this.selectedFamilyIndex + 1) % this.localFamilies.length;
        },
        processFamilies() {
            const familyInfoById = new Map(
                (this.familyInfo || []).map(info => [info.familyID, info])
            );

            this.localFamilies = (this.families || []).map(family => {
                const info = familyInfoById.get(family.familyID) || {};
                const husbandPlaceholder = family.husbandPlaceholder || info.husbandPlaceholder;
                const wifePlaceholder = family.wifePlaceholder || info.wifePlaceholder;
                const husbandName = isLivingPlaceholder(husbandPlaceholder)
                    ? this.$t('privacy.livingPlaceholder')
                    : info.husbandName
                        || [family.husbandName, family.husbandLastName].filter(Boolean).join(' ')
                        || this.$t('familySection.unknownFather');
                const wifeName = isLivingPlaceholder(wifePlaceholder)
                    ? this.$t('privacy.livingPlaceholder')
                    : info.wifeName
                        || [family.wifeName, family.wifeLastName].filter(Boolean).join(' ')
                        || this.$t('familySection.unknownMother');
                const children = (info.children || []).map(child => {
                    if (isLivingPlaceholder(child)) {
                        return {
                            ...child,
                            name: this.$t('privacy.livingPlaceholder'),
                            years: '',
                            photoSrc: '',
                            sex: 'unknown',
                            href: 'javascript:void(0)'
                        };
                    }

                    return {
                        ...child,
                        years: child.years || formatYears(
                            child.birthdatetr,
                            child.altbirthdatetr,
                            child.deathdatetr,
                            child.burialdatetr,
                            child.living
                        ),
                        photoSrc: child.photoSrc || getPhotoSrcWithFallback({ sex: child.sex }),
                        href: child.href || this.localePath(`/person/${child.personID}`)
                    };
                });

                children.sort((left, right) => {
                    const leftDate = extractSortableDate(left.birthdatetr) || extractSortableDate(left.altbirthdatetr);
                    const rightDate = extractSortableDate(right.birthdatetr) || extractSortableDate(right.altbirthdatetr);
                    if (leftDate && rightDate) return leftDate - rightDate;
                    if (leftDate) return -1;
                    if (rightDate) return 1;
                    return 0;
                });

                return {
                    ...family,
                    husbandName,
                    wifeName,
                    husbandYears: family.husbandYears || '',
                    wifeYears: family.wifeYears || '',
                    husbandPhotoSrc: isLivingPlaceholder(husbandPlaceholder)
                        ? ''
                        : info.husbandPhotoSrc
                            || (family.husband === this.personID ? this.person.profileImage : null)
                            || getPhotoSrcWithFallback({ sex: 'M' }),
                    wifePhotoSrc: isLivingPlaceholder(wifePlaceholder)
                        ? ''
                        : info.wifePhotoSrc
                            || (family.wife === this.personID ? this.person.profileImage : null)
                            || getPhotoSrcWithFallback({ sex: 'F' }),
                    husbandHref: family.husband ? this.localePath(`/person/${family.husband}`) : 'javascript:void(0)',
                    wifeHref: family.wife ? this.localePath(`/person/${family.wife}`) : 'javascript:void(0)',
                    husbandPlaceholder,
                    wifePlaceholder,
                    spouseID: family.husband === this.personID ? family.wife : family.husband,
                    children
                };
            });
        }
    }
};
</script>


<style scoped>
.margin-fix {
    margin-bottom: -28px;
}

.flex-wrap {
    display: flex;
    flex-direction: column;
    gap: 32px;
}

.FamilySection_heading {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
}

.FamilySection_daboville_toggle {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    padding: 9px 14px;
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: var(--border-radius-pill);
    background: rgba(255, 255, 255, 0.1);
    color: var(--brand-text-light);
    font-size: 13px;
    font-weight: 700;
    white-space: nowrap;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;
}

.FamilySection_daboville_toggle:hover {
    border-color: rgba(255, 255, 255, 0.62);
    background: rgba(255, 255, 255, 0.18);
}

.FamilySection_daboville_toggle:focus-visible {
    outline: 2px solid var(--brand-color-3);
    outline-offset: 2px;
}

.FamilySection_descendant_tree {
    margin-top: 52px;
    margin-bottom: 48px;
}

.FamilySection_daboville_container {
    width: 100%;
    margin-top: 52px;
    margin-bottom: 48px;
}

@media (max-width: 768px) {
    .FamilySection_heading {
        align-items: flex-start;
        flex-direction: column;
    }

    .FamilySection_daboville_toggle {
        align-self: flex-end;
    }
}

.FamilySection_children_cards {
    display: flex;
    flex-direction: column;
    gap: 32px;
}

.spouse-link {
    color: var(--brand-color-3);
    font-size: 24px;
    font-weight: 600;
    line-height: 125%;
}

.spouse-link:hover {
    text-decoration: underline;
}

.TitleBtnGroup {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 32px;
    background-color: var(--brand-bg-1);
    border-radius: var(--border-radius-xl);
    box-shadow: var(--shadow-card-sm);
    padding: 96px;
}

.ImageDivider_container {
    background: url('@/assets/noise128x128.png'), linear-gradient(to bottom, var(--brand-bg-8) 50%, var(--brand-bg-2) 50%);
    background-repeat: repeat;
    background-size: auto;
    transition: var(--transition-bounce);
}

.ImageDivider_wrapper {
    padding-left: 20px;
    padding-right: 20px;
}

.PersonCard--default {
    width: 100%;
    max-width: calc(33% - 12px);
}

/* .BtnPrimary {
    background-color: var(--brand-color-3);
    color: var(--brand-text-dark);
    border: 2px solid var(--brand-color-3);
    box-shadow: 0px 0px 0px 0px var(--brand-color-3);
}

.BtnPrimary:hover {
    box-shadow: 0px 0px 0px 4px var(--brand-color-3);
} */
</style>
